<template>
  <div class="d-flex flex-column candidate-detail-page">
    <HeaderNavbar :title="pageTitle" titleSize="sm" :breadcrumbs="listBreadcrumbs" :no-bottom-margin="true">
      <template #right-side v-if="selectedApplication">
        <div class="candidate-header-actions">
          <LeadButton
            variant="success"
            :icon-start="IconCheck"
            :loading="isActionLoading(selectedApplication, 'hired')"
            :disabled="isActionDisabled(selectedApplication, 'hired')"
            @click="promptAction(selectedApplication, 'hired')">
            {{ $t('candidate.detail.hire_button') }}
          </LeadButton>
          <LeadButton
            variant="outline-danger"
            :icon-start="IconX"
            :loading="isActionLoading(selectedApplication, 'closed')"
            :disabled="isActionDisabled(selectedApplication, 'closed')"
            @click="promptAction(selectedApplication, 'closed')">
            {{ $t('candidate.detail.close_button') }}
          </LeadButton>
          <LeadButton variant="white" :icon-start="IconBan" :disabled="Boolean(savingKey)" @click="openDiscardDialog">
            {{ $t('candidate.detail.discard_button') }}
          </LeadButton>
        </div>
      </template>

      <template #bottom v-if="candidate && selectedApplication">
        <div>
          <div class="candidate-header-contact">
            <div class="candidate-header-contact-item">
              <IconMail :size="16" class="text-muted" />
              <a :href="`mailto:${candidate.email}`">{{ candidate.email }}</a>
            </div>
            <div class="candidate-header-contact-item" v-if="candidate.phone">
              <IconPhone :size="16" class="text-muted" />
              <a :href="phoneHref">{{ candidate.phone }}</a>
            </div>
            <div class="candidate-header-contact-item text-muted" v-else>
              <IconPhone :size="16" />
              <span>{{ $t('candidate.detail.no_phone') }}</span>
            </div>
          </div>

          <div class="candidate-header-stats">
            <div class="candidate-header-stat">
              <span class="candidate-header-stat-label">{{ $t('candidate.detail.applied_on_label') }}</span>
              <span class="candidate-header-stat-value">{{ formatDateTime(selectedApplication.submitted_at) }}</span>
            </div>
            <div class="candidate-header-stat">
              <span class="candidate-header-stat-label">{{ $t('candidate.detail.application_label') }}</span>
              <span class="candidate-header-stat-value">{{ orderLabel(selectedApplication) }}</span>
            </div>
            <div class="candidate-header-stat">
              <span class="candidate-header-stat-label">{{ $t('candidate.detail.publication_label_short') }}</span>
              <span class="candidate-header-stat-value">{{ publicationLabel(selectedApplication) }}</span>
            </div>
          </div>
        </div>
      </template>
    </HeaderNavbar>

    <div v-if="isLoading" class="text-center text-muted py-5">
      <div class="spinner-border" role="status"></div>
      <p>{{ $t('candidate.detail.loading') }}</p>
    </div>

    <div v-else-if="loadError" class="py-5">
      <EmptyState
        :title="loadError === 'not_found' ? $t('candidate.detail.not_found_title') : $t('candidate.detail.error_title')"
        :subtitle="
          loadError === 'not_found' ? $t('candidate.detail.not_found_text') : $t('candidate.detail.load_error')
        ">
        <LeadButton variant="white" @click="loadCandidate">{{ $t('candidate.detail.retry') }}</LeadButton>
      </EmptyState>
    </div>

    <SidebarLayout v-else-if="candidate" sidebar-width="280px">
      <template #sidebar>
        <div class="candidate-sidebar">
          <button type="button" class="back-link" @click="goBack">
            <IconArrowLeft :size="16" />
            {{ $t('candidate.detail.back_to_applications') }}
          </button>

          <div class="sidebar-section" v-if="orderedApplications.length">
            <div class="sidebar-section-label">{{ $t('candidate.detail.applications') }}</div>
            <button
              type="button"
              class="sidebar-item"
              v-for="application in orderedApplications"
              :key="applicationKey(application)"
              :class="{ 'sidebar-item--active': isSelected(application) }"
              @click="selectApplication(application)">
              <IconFileText :size="16" class="sidebar-item-icon" />
              <span class="sidebar-item-body">
                <span class="sidebar-item-title text-truncate">{{ orderLabel(application) }}</span>
                <span class="sidebar-item-sub text-truncate">{{ publicationLabel(application) }}</span>
              </span>
              <span class="badge sidebar-item-badge" :class="laneBadgeClass(application.status_lane_key)">
                {{ laneName(application.status_lane_key) }}
              </span>
            </button>
          </div>

          <div class="sidebar-section" v-if="selectedApplication">
            <div class="sidebar-section-label">{{ $t('candidate.detail.sections') }}</div>
            <button
              type="button"
              class="sidebar-item"
              :class="{ 'sidebar-item--active': activeSection === 'answers' }"
              @click="scrollToSection('answers')">
              <IconClipboardList :size="16" class="sidebar-item-icon" />
              <span class="sidebar-item-title">{{ $t('candidate.detail.form_answers') }}</span>
            </button>
            <button
              type="button"
              class="sidebar-item"
              :class="{ 'sidebar-item--active': activeSection === 'timeline' }"
              @click="scrollToSection('timeline')">
              <IconActivity :size="16" class="sidebar-item-icon" />
              <span class="sidebar-item-title">{{ $t('candidate.detail.form_events') }}</span>
            </button>
            <button
              type="button"
              class="sidebar-item"
              :class="{ 'sidebar-item--active': activeSection === 'status' }"
              @click="scrollToSection('status')">
              <IconHistory :size="16" class="sidebar-item-icon" />
              <span class="sidebar-item-title">{{ $t('candidate.detail.status_history') }}</span>
            </button>
            <button
              type="button"
              class="sidebar-item"
              :class="{ 'sidebar-item--active': activeSection === 'fields' }"
              @click="scrollToSection('fields')">
              <IconListDetails :size="16" class="sidebar-item-icon" />
              <span class="sidebar-item-title">{{ $t('candidate.fields.title') }}</span>
            </button>
            <button type="button" class="sidebar-item sidebar-item--disabled" disabled>
              <IconNotes :size="16" class="sidebar-item-icon" />
              <span class="sidebar-item-title">{{ $t('candidate.detail.notes') }}</span>
            </button>
            <button type="button" class="sidebar-item sidebar-item--disabled" disabled>
              <IconPaperclip :size="16" class="sidebar-item-icon" />
              <span class="sidebar-item-title">{{ $t('candidate.detail.files') }}</span>
            </button>
          </div>

          <div class="sidebar-section">
            <div class="sidebar-section-label">{{ $t('candidate.detail.contact') }}</div>
            <div class="sidebar-contact-item">
              <IconMail :size="16" class="text-muted" />
              <a :href="`mailto:${candidate.email}`" class="text-truncate">{{ candidate.email }}</a>
            </div>
            <div class="sidebar-contact-item" v-if="candidate.phone">
              <IconPhone :size="16" class="text-muted" />
              <a :href="phoneHref">{{ candidate.phone }}</a>
            </div>
            <div class="sidebar-contact-item text-muted" v-else>
              <IconPhone :size="16" />
              <span>{{ $t('candidate.detail.no_phone') }}</span>
            </div>
            <div class="sidebar-contact-item">
              <IconClock :size="16" class="text-muted" />
              <span class="text-truncate">{{
                contactPreferenceLabel || $t('candidate.detail.no_contact_preference')
              }}</span>
            </div>
          </div>
        </div>
      </template>

      <div class="candidate-content" v-if="selectedApplication">
        <div class="candidate-content-columns">
          <div class="card candidate-section" ref="answersSectionRef">
            <div class="card-body">
              <h4 class="candidate-section-title">{{ $t('candidate.detail.form_answers') }}</h4>
              <div class="form-answers-wrapper" v-if="answersFor(selectedApplication).length">
                <table class="form-answers">
                  <colgroup>
                    <col class="form-answers-col-question" />
                    <col />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>{{ $t('candidate.detail.question_column') }}</th>
                      <th>{{ $t('candidate.detail.answer_column') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="answer in answersFor(selectedApplication)" :key="answer.question_id">
                      <td class="form-question">{{ answer.question_friendly || answer.question }}</td>
                      <td class="form-answer">{{ formatAnswer(answer.answer_friendly ?? answer.answer) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p class="text-muted mb-0" v-else>{{ $t('candidate.detail.no_answers') }}</p>
            </div>
          </div>

          <div class="candidate-content-side">
            <div class="card candidate-section" ref="timelineSectionRef">
              <div class="card-body">
                <h4 class="candidate-section-title">{{ $t('candidate.detail.form_events') }}</h4>
                <ul class="form-timeline" v-if="allEventsFor(selectedApplication).length">
                  <li
                    class="form-timeline-item"
                    v-for="(event, index) in visibleEvents"
                    :key="`${event.event}-${event.section_index}-${index}`">
                    <span class="form-timeline-icon" :class="eventIconClass(event)">
                      <component :is="eventIcon(event)" :size="14" />
                    </span>
                    <div class="form-timeline-content">
                      <span class="form-timeline-label">{{ eventLabel(event) }}</span>
                      <span
                        class="form-timeline-answer"
                        v-if="event.event === 'section_answered' && event.answer_friendly">
                        {{ formatAnswer(event.answer_friendly) }}
                      </span>
                      <span class="form-timeline-time">{{ formatDateTime(event.client_timestamp) }}</span>
                    </div>
                  </li>
                </ul>
                <p class="text-muted mb-0" v-else>{{ $t('candidate.detail.no_events') }}</p>
                <button
                  type="button"
                  class="btn btn-link btn-sm px-0 show-more-toggle"
                  v-if="allEventsFor(selectedApplication).length > eventsPageSize"
                  @click="eventsShowAll = !eventsShowAll">
                  {{ eventsShowAll ? $t('candidate.detail.show_less') : $t('candidate.detail.show_more') }}
                </button>
              </div>
            </div>

            <div class="card candidate-section" ref="statusSectionRef">
              <div class="card-body">
                <h4 class="candidate-section-title">{{ $t('candidate.detail.status_history') }}</h4>
                <ul class="form-timeline" v-if="statusHistoryFor(selectedApplication).length">
                  <li
                    class="form-timeline-item"
                    v-for="(entry, index) in statusHistoryFor(selectedApplication)"
                    :key="`${entry.status_lane_key}-${entry.changed_at}-${index}`">
                    <span class="form-timeline-icon">
                      <IconFlag v-if="entry.previous_status_lane_key === null" :size="14" />
                      <IconArrowRight v-else :size="14" />
                    </span>
                    <div class="form-timeline-content">
                      <span class="form-timeline-label" v-if="entry.previous_status_lane_key === null">
                        {{ $t('candidate.detail.status_created', { status: laneName(entry.status_lane_key) }) }}
                      </span>
                      <span class="form-timeline-label" v-else>
                        {{
                          entry.previous_status_lane_key
                            ? $t('candidate.detail.status_change', {
                                from: laneName(entry.previous_status_lane_key),
                                to: laneName(entry.status_lane_key),
                              })
                            : laneName(entry.status_lane_key)
                        }}
                      </span>
                      <span class="form-timeline-time">{{ formatDateTime(entry.changed_at) }}</span>
                    </div>
                  </li>
                </ul>
                <p class="text-muted mb-0" v-else>{{ $t('candidate.detail.no_status_history') }}</p>
              </div>
            </div>

            <div class="card candidate-section" ref="fieldsSectionRef">
              <div class="card-body">
                <h4 class="candidate-section-title">{{ $t('candidate.fields.title') }}</h4>
                <CandidateCustomFields
                  v-if="candidate"
                  :candidate-entity-id="candidate.candidate_entity_id"
                  :values="candidate.custom_fields"
                  @update:values="onCustomFieldsUpdated" />
              </div>
            </div>

            <div class="card candidate-section" ref="filesSectionRef">
              <div class="card-body">
                <h4 class="candidate-section-title">{{ $t('candidate.detail.files') }}</h4>
                <ul class="candidate-files" v-if="filesFor(selectedApplication).length">
                  <li class="candidate-file-item" v-for="file in filesFor(selectedApplication)" :key="file.file_sha256">
                    <IconFileText :size="16" class="candidate-file-icon" />
                    <span class="candidate-file-body">
                      <span class="candidate-file-name text-truncate">{{ file.file_name }}</span>
                      <span class="candidate-file-sub text-truncate">{{ formatDateTime(file.uploaded_at) }}</span>
                    </span>
                    <LeadButton
                      variant="white"
                      :icon-start="IconDownload"
                      :loading="downloadingFileSha256 === file.file_sha256"
                      @click="downloadFile(file)">
                      {{ $t('candidate.detail.download_file') }}
                    </LeadButton>
                  </li>
                </ul>
                <p class="text-muted mb-0" v-else>{{ $t('candidate.detail.no_files') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p class="text-muted p-4 mb-0" v-else>{{ $t('candidate.detail.no_applications') }}</p>
    </SidebarLayout>

    <LiConfirmationDialog
      v-model:dialogShow="confirmDialogShow"
      :confirmButtonLabel="confirmButtonLabel"
      @onContinue="confirmAction">
      <h1 class="mb-3">{{ confirmTitle }}</h1>
      <p>{{ confirmText }}</p>
    </LiConfirmationDialog>

    <DiscardCandidateDialog
      v-if="candidate && selectedApplication"
      v-model:dialogShow="discardDialogShow"
      :candidate-entity-id="candidate.candidate_entity_id"
      :order-id="selectedApplication.order_id"
      :candidate-name="pageTitle"
      @discarded="handleCandidateDiscarded"
      @fully-deleted="handleCandidateFullyDeleted" />
  </div>
</template>
<script setup lang="ts">
import { getCandidate, fetchSwimlanes, closeCandidate, hireCandidate, downloadCandidateFile } from '@/api/candidates';
import { ClientError } from '@/api/client';
import type { ICandidate, AnswerValue, FormEvent, CandidateFieldValue, CandidateFile } from '@/types/candidate';
import type { Swimlane, SwimlaneSystemRole } from '@/types/kanban';
import DiscardCandidateDialog from '@/components/candidate/DiscardCandidateDialog.vue';
import HeaderNavbar from '@/components/header/HeaderNavbar.vue';
import SidebarLayout from '@/components/common/SidebarLayout.vue';
import EmptyState from '@/components/common/EmptyState.vue';
import LeadButton from '@/components/generic/LeadButton.vue';
import CandidateCustomFields from '@/components/candidate/CandidateCustomFields.vue';
import { ConfirmationDialog as LiConfirmationDialog } from '@prospective/lithium';
import translator from '@/locale/translator';
import { formatDateTime } from '@/filters/dateFormat';
import { useCustomerFeaturesStore } from '@/stores/customerFeatures';
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import {
  IconMail,
  IconPhone,
  IconArrowLeft,
  IconFileText,
  IconClipboardList,
  IconActivity,
  IconHistory,
  IconListDetails,
  IconNotes,
  IconPaperclip,
  IconX,
  IconBan,
  IconCheck,
  IconPlayerPlay,
  IconEye,
  IconPencil,
  IconClock,
  IconArrowRight,
  IconFlag,
  IconDownload,
} from '@tabler/icons-vue';

type CandidateApplication = ICandidate['applications'][number];
type MoveRole = Extract<SwimlaneSystemRole, 'closed' | 'hired' | 'discarded'>;
type SectionKey = 'answers' | 'timeline' | 'status' | 'fields' | 'files';
type CandidateAction = Extract<MoveRole, 'closed' | 'hired'>;

const route = useRoute();
const router = useRouter();
const toast = useToast();
const featureStore = useCustomerFeaturesStore();

const { id } = route.params;

/* --------------------------------- STATE ----------------------------------- */
const candidate = ref<ICandidate | null>(null);
const swimlanes = ref<Swimlane[]>([]);
const isLoading = ref(true);
const loadError = ref<'not_found' | 'error' | null>(null);
const selectedKey = ref<string | null>(null);
const savingKey = ref<string | null>(null);

const activeSection = ref<SectionKey>('answers');
const answersSectionRef = ref<HTMLElement | null>(null);
const timelineSectionRef = ref<HTMLElement | null>(null);
const statusSectionRef = ref<HTMLElement | null>(null);
const fieldsSectionRef = ref<HTMLElement | null>(null);
const filesSectionRef = ref<HTMLElement | null>(null);

const eventsShowAll = ref(false);
const eventsPageSize = 6;

const downloadingFileSha256 = ref<string | null>(null);

const confirmDialogShow = ref(false);
const pendingApplication = ref<CandidateApplication | null>(null);
const pendingAction = ref<CandidateAction | null>(null);

const discardDialogShow = ref(false);

/* -------------------------------- COMPUTED --------------------------------- */
const pageTitle = computed(() => {
  if (!candidate.value) return `Candidate #${id}`;
  const name = [candidate.value.firstname, candidate.value.lastname].filter(Boolean).join(' ').trim();
  return name || translator.get('candidate.detail.unnamed_candidate');
});

// the phone is displayed formatted with spaces, which a tel: URI must not contain
const phoneHref = computed(() => `tel:${(candidate.value?.phone ?? '').replace(/[^\d+]/g, '')}`);

const listBreadcrumbs = computed(() => [
  { label: translator.get('nav.home'), path: { name: 'home' } },
  { label: pageTitle.value, path: { name: 'candidate.detail', params: { id } } },
]);

// all applications are shown the same way, most recently submitted first
const orderedApplications = computed(() =>
  [...(candidate.value?.applications ?? [])].sort(
    (a, b) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime()
  )
);

const selectedApplication = computed(
  () => orderedApplications.value.find((application) => applicationKey(application) === selectedKey.value) ?? null
);

const visibleEvents = computed(() => {
  if (!selectedApplication.value) return [];
  const events = allEventsFor(selectedApplication.value);
  return eventsShowAll.value ? events : events.slice(0, eventsPageSize);
});

const actionTitleKeys: Record<CandidateAction, string> = {
  closed: 'candidate.detail.close_title',
  hired: 'candidate.detail.hire_title',
};

const actionTextKeys: Record<CandidateAction, string> = {
  closed: 'candidate.detail.close_text',
  hired: 'candidate.detail.hire_text',
};

const actionButtonKeys: Record<CandidateAction, string> = {
  closed: 'candidate.detail.close_button',
  hired: 'candidate.detail.hire_button',
};

const actionSuccessKeys: Record<CandidateAction, string> = {
  closed: 'candidate.detail.close_success',
  hired: 'candidate.detail.hire_success',
};

const actionErrorKeys: Record<CandidateAction, string> = {
  closed: 'candidate.detail.close_error',
  hired: 'candidate.detail.hire_error',
};

const confirmTitle = computed(() => (pendingAction.value ? translator.get(actionTitleKeys[pendingAction.value]) : ''));

const confirmText = computed(() => {
  if (!pendingApplication.value || !pendingAction.value) return '';
  return translator.get(actionTextKeys[pendingAction.value], {
    name: pageTitle.value,
    id: pendingApplication.value.order_id,
  });
});

const confirmButtonLabel = computed(() =>
  pendingAction.value ? translator.get(actionButtonKeys[pendingAction.value]) : ''
);

// contact_preference is stored per candidate, not per application (it's collected once, not per
// session), so it's shown once in the sidebar regardless of which application is selected
const contactPreferenceLabel = computed(() => {
  const preference = candidate.value?.contact_preference;
  if (!preference?.preferred_contact_time) return null;
  if (preference.preferred_contact_time === 'custom') return preference.preferred_contact_time_custom;
  return translator.get(`candidate.detail.contact_time_${preference.preferred_contact_time}`);
});

/* --------------------------------- METHODS -------------------------------- */
const applicationKey = (application: CandidateApplication) => `${application.order_id}-${application.session_id}`;

const isSelected = (application: CandidateApplication) => applicationKey(application) === selectedKey.value;

const selectApplication = (application: CandidateApplication) => {
  selectedKey.value = applicationKey(application);
  activeSection.value = 'answers';
  eventsShowAll.value = false;
};

const goBack = () => {
  // a link opened in a new tab or pasted elsewhere has no in-app history to go back to
  if (window.history.state?.back) {
    router.back();
    return;
  }

  const orderId = selectedApplication.value?.order_id;
  router.push(orderId ? { name: 'order.detail.kanban', params: { id: orderId } } : { name: 'orders.list' });
};

const sectionRefs: Record<SectionKey, typeof answersSectionRef> = {
  answers: answersSectionRef,
  timeline: timelineSectionRef,
  status: statusSectionRef,
  fields: fieldsSectionRef,
  files: filesSectionRef,
};

const orderLabel = (application: CandidateApplication) =>
  application.order_title || translator.get('candidate.detail.order_label', { id: application.order_id });

const publicationLabel = (application: CandidateApplication) =>
  application.publication_name ||
  translator.get('candidate.detail.publication_label', { id: application.publication_id });

const onCustomFieldsUpdated = (values: CandidateFieldValue[]) => {
  if (candidate.value) candidate.value.custom_fields = values;
};

const scrollToSection = (section: SectionKey) => {
  activeSection.value = section;
  sectionRefs[section].value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const answersFor = (application: CandidateApplication) => application.answers ?? [];

const allEventsFor = (application: CandidateApplication) =>
  [...(application.events ?? [])].sort(
    (a, b) => new Date(b.client_timestamp).getTime() - new Date(a.client_timestamp).getTime()
  );

const statusHistoryFor = (application: CandidateApplication) =>
  [...(application.status_history ?? [])].sort(
    (a, b) => new Date(b.changed_at).getTime() - new Date(a.changed_at).getTime()
  );

const filesFor = (application: CandidateApplication | null) => application?.files ?? [];

const downloadFile = async (file: CandidateFile) => {
  if (!candidate.value) return;

  downloadingFileSha256.value = file.file_sha256;
  try {
    const blob = await downloadCandidateFile(candidate.value.candidate_entity_id, file.file_sha256);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.file_name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading candidate file:', error);
    toast.error(translator.get('candidate.detail.file_download_error'));
  } finally {
    downloadingFileSha256.value = null;
  }
};

const formatAnswer = (value: AnswerValue) => (Array.isArray(value) ? value.join(', ') : value);

// `funnel_opened` is the pre-rename spelling of `form_opened` and must render identically: it is
// what every pre-rename tracking record opens with, and what a POPS bundle cached in a candidate's
// browser keeps sending until its template is rebuilt.
const eventIcon = (event: FormEvent) => {
  switch (event.event) {
    case 'form_opened':
    case 'funnel_opened':
      return IconPlayerPlay;
    case 'section_answered':
      return IconPencil;
    case 'section_viewed':
      return IconEye;
    case 'section_back':
      return IconArrowLeft;
    default:
      return IconClock;
  }
};

const eventIconClass = (event: FormEvent) => {
  switch (event.event) {
    case 'form_opened':
    case 'funnel_opened':
      return 'form-timeline-icon--opened';
    case 'section_answered':
      return 'form-timeline-icon--answered';
    case 'section_viewed':
      return 'form-timeline-icon--viewed';
    case 'section_back':
      return 'form-timeline-icon--back';
    default:
      return '';
  }
};

const eventLabel = (event: FormEvent) => {
  const section = event.question_friendly || event.section_key;
  switch (event.event) {
    case 'form_opened':
    case 'funnel_opened':
      return translator.get('candidate.detail.event_form_opened');
    case 'section_viewed':
      return translator.get('candidate.detail.event_section_viewed', { section });
    case 'section_answered':
      return translator.get('candidate.detail.event_section_answered', { section });
    case 'section_back':
      // tracked before the step index moves, so the section named is the one being left
      return translator.get('candidate.detail.event_section_back', { section });
    default:
      // an event PJAS added that LEAD has not been taught yet — the raw key is a poor label, but it
      // is better than an empty row, and FormEventType makes the gap visible at build time
      return event.event;
  }
};

const laneName = (laneKey: string | null) =>
  swimlanes.value.find((lane) => lane.lane_key === laneKey)?.name ?? laneKey ?? '';

const laneBadgeClass = (laneKey: string) => {
  const lane = swimlanes.value.find((l) => l.lane_key === laneKey);
  switch (lane?.system_role) {
    case 'hired':
      return 'bg-green-lt';
    case 'closed':
      return 'bg-red-lt';
    case 'discarded':
      return 'bg-secondary-lt';
    default:
      return 'bg-azure-lt';
  }
};

const isCurrentLaneRole = (application: CandidateApplication, role: CandidateAction) =>
  swimlanes.value.find((lane) => lane.lane_key === application.status_lane_key)?.system_role === role;

const isActionLoading = (application: CandidateApplication, action: CandidateAction) =>
  savingKey.value === `${applicationKey(application)}:${action}`;

const isActionDisabled = (application: CandidateApplication, action: CandidateAction) =>
  Boolean(savingKey.value) || isCurrentLaneRole(application, action);

const promptAction = (application: CandidateApplication, action: CandidateAction) => {
  pendingApplication.value = application;
  pendingAction.value = action;
  confirmDialogShow.value = true;
};

const openDiscardDialog = () => {
  discardDialogShow.value = true;
};

const handleCandidateDiscarded = async () => {
  candidate.value = await getCandidate(id as string);
};

const handleCandidateFullyDeleted = () => {
  const canGoBack = Boolean(window.history.state?.back);
  if (canGoBack) router.back();
  else router.push({ name: 'orders.list' });
};

const confirmAction = async () => {
  const application = pendingApplication.value;
  const action = pendingAction.value;
  if (!candidate.value || !application || !action) return;

  const key = `${applicationKey(application)}:${action}`;
  savingKey.value = key;
  const candidateEntityId = candidate.value.candidate_entity_id;

  try {
    const requestOptions = { skipDefaultErrorHandler: true };
    if (action === 'closed') await closeCandidate(candidateEntityId, application.order_id, requestOptions);
    else if (action === 'hired') await hireCandidate(candidateEntityId, application.order_id, requestOptions);
  } catch (error) {
    console.error('Error updating candidate application status:', error);
    toast.error(translator.get(actionErrorKeys[action]));
    savingKey.value = null;
    pendingApplication.value = null;
    pendingAction.value = null;
    return;
  }

  savingKey.value = null;
  pendingApplication.value = null;
  pendingAction.value = null;
  toast.success(translator.get(actionSuccessKeys[action]));

  // a failed refetch here would incorrectly look like the action itself failed, so it gets its
  // own try — the mutation already succeeded, just keep the stale view on failure
  try {
    candidate.value = await getCandidate(id as string);
  } catch (error) {
    console.error('Error refetching candidate after status change:', error);
  }
};

/* --------------------------------- METHODS (DATA LOADING) ------------------ */
const loadCandidate = async () => {
  try {
    isLoading.value = true;
    loadError.value = null;
    const [candidateData, swimlaneData] = await Promise.all([getCandidate(id as string), fetchSwimlanes()]);
    candidate.value = candidateData;
    swimlanes.value = swimlaneData.sort((a, b) => a.position - b.position);

    // select the application matching ?orderId= when arriving from an order's kanban board,
    // otherwise default to the most recently submitted application
    const requestedOrderId = route.query.orderId;
    const match = requestedOrderId
      ? orderedApplications.value.find((application) => String(application.order_id) === String(requestedOrderId))
      : undefined;
    const initialApplication = match ?? orderedApplications.value[0];
    if (initialApplication) selectApplication(initialApplication);
  } catch (error) {
    console.error('Error fetching candidate:', error);
    loadError.value = error instanceof ClientError && error.response.status === 404 ? 'not_found' : 'error';
  } finally {
    isLoading.value = false;
  }
};

/* ------------------------------ LIFECYCLE HOOKS ----------------------------- */
onMounted(async () => {
  await featureStore.fetchFeatures();
  if (!featureStore.featureEcDataVault) {
    router.replace({ name: 'home' });
    return;
  }

  await loadCandidate();
});
</script>
<style scoped>
.candidate-sidebar {
  display: flex;
  flex-direction: column;
  padding: 16px 12px;
  gap: 20px;
}

.back-link {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border: none;
  background: none;
  color: #1570ef;
  font-weight: 600;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  width: fit-content;
}

.back-link:hover {
  text-decoration: underline;
}

.sidebar-section {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sidebar-section-label {
  padding: 0 8px 6px;
  color: #98a2b3;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.sidebar-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border: none;
  background: none;
  border-radius: 6px;
  text-align: left;
  cursor: pointer;
  min-width: 0;
}

.sidebar-item:hover {
  background: #eaecf0;
}

.sidebar-item--active {
  background: #eff8ff;
  color: #1570ef;
}

.sidebar-item--active:hover {
  background: #eff8ff;
}

.sidebar-item--disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.sidebar-item--disabled:hover {
  background: none;
}

.sidebar-item-icon {
  flex: 0 0 auto;
  color: inherit;
}

.sidebar-item-title {
  font-size: 13px;
  font-weight: 600;
  color: inherit;
  min-width: 0;
}

.sidebar-item-body {
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex: 1;
  min-width: 0;
}

.sidebar-item-sub {
  font-size: 12px;
  color: #667085;
}

.sidebar-item-badge {
  flex: 0 0 auto;
  font-size: 11px;
}

.sidebar-contact-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  font-size: 13px;
  min-width: 0;
}

.sidebar-contact-item a,
.sidebar-contact-item span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.candidate-content {
  padding: 24px;
}

.candidate-header-contact {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  font-size: 13px;
  margin-bottom: 8px;
}

.candidate-header-contact-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.candidate-header-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 32px;
  width: 100%;
}

.candidate-header-stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.candidate-header-stat-label {
  font-size: 12px;
  color: #98a2b3;
}

.candidate-header-stat-value {
  font-size: 14px;
  font-weight: 600;
  color: #101828;
  overflow-wrap: break-word;
  word-break: break-word;
}

.candidate-header-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex: 0 0 auto;
}

.candidate-content-columns {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: flex-start;
}

.candidate-content-columns > .candidate-section {
  flex: 1 1 420px;
  min-width: 0;
}

.candidate-content-side {
  display: flex;
  flex-direction: column;
  gap: 24px;
  flex: 1 1 360px;
  min-width: 0;
}

.candidate-section-title {
  font-size: 14px;
  font-weight: 600;
  color: #101828;
  margin: 0 0 16px;
}

.show-more-toggle {
  text-decoration: none;
}

.form-answers-wrapper {
  overflow-x: auto;
  margin: 0;
}

.form-answers {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}

.form-answers-col-question {
  width: 280px;
}

.form-answers th {
  text-align: left;
  padding: 0 16px 8px 0;
  color: #667085;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-bottom: 1px solid #d0d5dd;
}

.form-answers td {
  padding: 10px 16px 10px 0;
  border-bottom: 1px solid #eaecf0;
  vertical-align: top;
}

.form-answers tr:last-child td {
  border-bottom: none;
}

.form-question {
  color: #101828;
  font-weight: 600;
}

.form-answer {
  color: #475467;
}

.form-timeline {
  list-style: none;
  margin: 0 0 8px;
  padding: 0;
}

.form-timeline-item {
  position: relative;
  display: flex;
  gap: 12px;
  padding-bottom: 16px;
}

.form-timeline-item:last-child {
  padding-bottom: 0;
}

.form-timeline-item:not(:last-child)::before {
  content: '';
  position: absolute;
  left: 11px;
  top: 24px;
  bottom: 0;
  width: 1px;
  background: #eaecf0;
}

.form-timeline-icon {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #f2f4f7;
  color: #667085;
  z-index: 1;
}

.form-timeline-icon--opened {
  background: #eff8ff;
  color: #2e90fa;
}

.form-timeline-icon--viewed {
  background: #f2f4f7;
  color: #667085;
}

.form-timeline-icon--answered {
  background: #ecfdf3;
  color: #12b76a;
}

.form-timeline-icon--back {
  background: #fffaeb;
  color: #f79009;
}

.form-timeline-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-top: 2px;
}

.form-timeline-label {
  color: #101828;
  font-weight: 600;
  font-size: 13px;
}

.form-timeline-answer {
  color: #475467;
  font-size: 13px;
}

.form-timeline-time {
  color: #98a2b3;
  font-size: 12px;
}

.candidate-files {
  list-style: none;
  margin: 0;
  padding: 0;
}

.candidate-file-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #eaecf0;
}

.candidate-file-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.candidate-file-icon {
  flex: 0 0 auto;
  color: #667085;
}

.candidate-file-body {
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex: 1;
  min-width: 0;
}

.candidate-file-name {
  font-size: 13px;
  font-weight: 600;
  color: #101828;
}

.candidate-file-sub {
  font-size: 12px;
  color: #667085;
}

.candidate-detail-page {
  height: 100vh;
  overflow: hidden;
}

/* SidebarLayout assumes a 60px header; ours is taller, so let it fill the actual remaining space instead */
.candidate-detail-page :deep(.sidebar-layout) {
  height: auto;
  flex: 1 1 auto;
  min-height: 0;
}
</style>
